import {
  FilterArea,
  useFilterAreaContentContext,
  useFilterAreaContext,
} from "#src/components/FilterArea";
import { useInfiniteListWorkerVersions } from "#src/components/hooks/adapters/useWorkers";
import {
  PillToggleGroup,
  PillToggleVariants,
  type PillToggleProps,
  StorageKeys,
} from "@validereinc/common-components";
import {
  WorkerVersionStatus,
  type WorkerVersionType,
} from "@validereinc/domain";
import { toTitleCaseString } from "@validereinc/utilities";
import React, { useEffect, useMemo } from "react";

export const WORKER_VERSIONS_FILTER_KEYS = {
  status: "status",
} as const;

export type WorkerVersionsFiltersType = {
  [WORKER_VERSIONS_FILTER_KEYS.status]?: string[];
};

function useWorkerVersionStatusCounts(workerId: string) {
  const { data, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useInfiniteListWorkerVersions(
      { workerId, filters: {}, pageSize: 25 },
      { enabled: Boolean(workerId) }
    );

  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return useMemo(() => {
    const allRows: WorkerVersionType[] =
      data?.pages?.flatMap((page) => page.data) ?? [];
    if (!data && isLoading) {
      return {
        pending: 0,
        active: 0,
        failed: 0,
        archived: 0,
        total: 0,
        isLoading: true,
      };
    }
    const counts = { pending: 0, active: 0, failed: 0, archived: 0 };
    for (const row of allRows) {
      if (row.status === WorkerVersionStatus.PENDING) counts.pending += 1;
      else if (row.status === WorkerVersionStatus.ACTIVE) counts.active += 1;
      else if (row.status === WorkerVersionStatus.FAILED) counts.failed += 1;
      else if (row.status === WorkerVersionStatus.ARCHIVED)
        counts.archived += 1;
    }
    return {
      ...counts,
      total: allRows.length - counts.archived,
      isLoading: isLoading || isFetchingNextPage,
    };
  }, [data, isLoading, isFetchingNextPage]);
}

export const WorkerVersionsTableTitleDecorationFilterArea = ({
  filterConfigStorageKey,
  workerId,
}: {
  workerId: string;
} & Pick<StorageKeys, "filterConfigStorageKey">) => {
  return (
    <FilterArea.Root
      storageKey={filterConfigStorageKey}
      defaultValues={{}}
    >
      <FilterArea.Container aria-label="Filters for Worker Versions">
        <FilterArea.Content>
          {() => (
            <WorkerVersionsTableTitleDecorationFilterAreaContent
              workerId={workerId}
            />
          )}
        </FilterArea.Content>
      </FilterArea.Container>
    </FilterArea.Root>
  );
};

function WorkerVersionsTableTitleDecorationFilterAreaContent({
  workerId,
}: {
  workerId: string;
}) {
  const statusCounts = useWorkerVersionStatusCounts(workerId);
  const { handleOnChange } = useFilterAreaContentContext();
  const { storedFilters: { status } = {} } = useFilterAreaContext<{
    status?: string[];
  }>();

  const pillToggles: PillToggleProps[] = [
    {
      name: "All",
      label: "All",
      value: "",
      isSelected: !status?.length || status?.includes(""),
      shouldSelectAll: true,
      count: statusCounts.total,
    },
    {
      name: WorkerVersionStatus.PENDING,
      label: toTitleCaseString(WorkerVersionStatus.PENDING, { intent: "ui" }),
      value: WorkerVersionStatus.PENDING,
      variant: PillToggleVariants.ATTENTION,
      isSelected: status?.includes(WorkerVersionStatus.PENDING) ?? false,
      count: statusCounts.pending,
    },
    {
      name: WorkerVersionStatus.ACTIVE,
      label: toTitleCaseString(WorkerVersionStatus.ACTIVE, { intent: "ui" }),
      value: WorkerVersionStatus.ACTIVE,
      variant: PillToggleVariants.GOOD,
      isSelected: status?.includes(WorkerVersionStatus.ACTIVE) ?? false,
      count: statusCounts.active,
    },
    {
      name: WorkerVersionStatus.FAILED,
      label: toTitleCaseString(WorkerVersionStatus.FAILED, { intent: "ui" }),
      value: WorkerVersionStatus.FAILED,
      variant: PillToggleVariants.FAILURE,
      isSelected: status?.includes(WorkerVersionStatus.FAILED) ?? false,
      count: statusCounts.failed,
    },
    {
      name: WorkerVersionStatus.ARCHIVED,
      label: toTitleCaseString(WorkerVersionStatus.ARCHIVED, { intent: "ui" }),
      value: WorkerVersionStatus.ARCHIVED,
      variant: PillToggleVariants.NEUTRAL,
      isSelected: status?.includes(WorkerVersionStatus.ARCHIVED) ?? false,
      count: statusCounts.archived,
    },
  ];

  return (
    <div
      style={{ marginRight: 8, display: "flex", gap: 8, alignItems: "center" }}
    >
      <PillToggleGroup
        name={WORKER_VERSIONS_FILTER_KEYS.status}
        pills={pillToggles}
        onChange={(val) =>
          handleOnChange(val, WORKER_VERSIONS_FILTER_KEYS.status)
        }
        isLoading={statusCounts.isLoading}
      />
    </div>
  );
}
